import { computed, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import CallLog from "@/services/call-log";
import type { CallLogResponse } from "@/dtos/CallLog.dto";

export function useCallLogBySid(
  callSid: Ref<string | null>,
  organizationId: Ref<string | undefined>,
  options?: { enabled?: Ref<boolean> }
) {
  const queryKey = computed(() => ["callLog", callSid.value, organizationId.value] as const);

  const enabled = computed(() => {
    if (!callSid.value || !organizationId.value) return false;
    return options?.enabled?.value ?? true;
  });

  return useQuery({
    queryKey,
    queryFn: async (): Promise<CallLogResponse | null> => {
      const sid = callSid.value;
      const orgId = organizationId.value;
      if (!sid || !orgId) return null;

      const result = await CallLog.getCallLogs({
        callSid: sid,
        organizationId: orgId,
        limit: 1,
      });

      if (result.data.status) {
        const data = result.data.data;
        return Array.isArray(data) && data.length > 0 ? (data[0] as CallLogResponse) : null;
      }

      throw new Error(result.data.msg || "Failed to load call log");
    },
    enabled,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: (query) => {
      const d = query.state.data as CallLogResponse | null | undefined;
      if (!d) return 5000;
      const s = d.analysisStatus;
      const ts = d.transcriptStatus;
      const processing =
        s === "pending" || s === "in_progress" || ts === "pending" || ts === "in_progress";
      const awaitingRecording =
        d.status === "completed" && d.saveRecording && !d.recordingSid && d.duration > 0;
      return processing || awaitingRecording ? 5000 : 0;
    },
  });
}
